/**
 * Vellatini PDP pair picker — state + DOM (blocks/vellatini-pair-picker.liquid).
 *
 * State: { mode: 'single' | 'pair', partnerKey }. Every amount is recomputed
 * client-side from the block's JSON blob with the SAME rule as the Discount
 * Function (amt = max allowance, pair = hero + partner − amt) so the badge,
 * chips, dialog and button never disagree with the cart.
 *
 * Horizon hooks (read-only, nothing in core theme JS is edited):
 *   - Buy button `[ref="addToCartButton"]` inside `.buy-buttons-block
 *     product-form-component` → relabelled by vellatini-pair-buy-button.js
 *     (type="button" in pair mode, so no native submit reaches product-form.js's
 *     document-level capture-phase on:submit). Our click handler on the
 *     product-form-component runs the add-both path (vellatini-pair-add.js);
 *     add-to-cart-component's own on:click animation still fires first.
 *   - Variant change = `shopify:product:select` (StandardEvents.productSelect,
 *     the listener vellatini-preorder-sync.js uses). product-form.js morphs the
 *     button from `detail.html` after the promise resolves, so the label is
 *     re-applied a beat later and the new original label is read from that
 *     fetched document, never from our relabelled node.
 *   - Details = native <dialog>.showModal(); Esc/×/scrim close.
 */

import { formatMoney } from '@theme/money-formatting';

/** @type {HTMLElement | null} */
let booted = null;
/** Re-render hook for the current picker; ab.js fires `hcab:prices` once a price-test map loads. */
let rerender = null;
document.addEventListener('hcab:prices', () => rerender?.());
// One init per root element. vellatini-sibling-swap.js replaces the root wholesale
// (data-vt-swap-key per product) and fires vellatini:pdp:swapped, so a sibling with
// partners gets a fresh init and a sibling without partners simply has no root.
function boot() {
  const root = document.querySelector('[data-vellatini-pair-picker]');
  if (root instanceof HTMLElement && root !== booted) {
    booted = root;
    init(root);
  }
}
boot();
document.addEventListener('vellatini:pdp:swapped', boot);

/** @param {HTMLElement} root */
async function init(root) {
  const data = JSON.parse(root.querySelector('[data-pair-picker]')?.textContent || '{}');
  if (!data.partners?.length) return;
  const [buyBtn, add] = await Promise.all([import(root.dataset.pairButtonModule), import(root.dataset.pairAddModule)]);

  const buyBox = root.closest('.pdp-bb') ?? document;
  const buyBlock = buyBox.querySelector('.buy-buttons-block');
  const formComponent = buyBlock?.querySelector('product-form-component');
  const form = formComponent?.querySelector('form[data-type="add-to-cart-form"]');
  const atcLine = root.querySelector('[data-pair-atc-line]');
  const buyButton = () => buyBlock?.querySelector('[ref="addToCartButton"]');
  const state = {
    mode: 'single',
    partnerKey: data.partners[0].key,
    heroPrice: data.hero.price,
    heroVariantId: data.hero.variantId,
    originalLabel: (() => { const b = buyButton(); return b ? buyBtn.labelNode(b)?.textContent ?? '' : ''; })(),
    busy: false,
  };

  // money_without_trailing_zeros parity ($299, not $299.00).
  const money = (cents) => formatMoney(cents, data.moneyFormat, data.currency).replace(/([.,])00(?!\d)/, '');
  const dollars = (cents) => `$${Math.round(cents / 100)}`;
  const partner = (key) => data.partners.find((p) => p.key === key) ?? data.partners[0];
  // Price-test parity (HC A/B price tests, 2026-09-12): the cart transform charges the
  // visitor's arm price, so every figure here must start from it too. ab.js exposes
  // window.__HCAB.price.get(variantId) → { a, b, c } for a treatment visitor, null for
  // control / untested variants / before the map loads (then we fall back to catalog
  // and `hcab:prices` re-renders once it lands).
  const effective = (variantId, fallback) => {
    const arm = window.__HCAB?.price?.get?.(String(variantId));
    const cents = arm && arm.b != null ? Math.round(parseFloat(arm.b) * 100) : NaN;
    return Number.isFinite(cents) ? cents : fallback;
  };
  const amounts = (p) => {
    const amt = Math.max(Number(data.hero.allowance) || 0, Number(p.allowance) || 0) * 100;
    const hero = effective(state.heroVariantId, state.heroPrice);
    const price = effective(p.variantId, p.price);
    const sum = hero + price;
    return { amt, sum, pair: sum - amt, incr: price - amt, hero, price };
  };
  const setText = (scope, selector, text) => {
    const el = scope.querySelector(selector);
    if (el) el.textContent = text;
  };

  // The line under the button belongs in the buy-buttons block's slot, not ours.
  if (atcLine && buyBlock) buyBlock.insertAdjacentElement('afterend', atcLine);

  function render() {
    const { amt, sum, pair, hero } = amounts(partner(state.partnerKey));
    root.dataset.pairState = state.mode; // CSS recedes the partner list while "This bag" is chosen
    root.querySelectorAll('[data-pair-mode]').forEach((o) => o.setAttribute('aria-checked', String(o.dataset.pairMode === state.mode)));
    setText(root, '[data-pair-a-amt]', money(hero));
    setText(root, '[data-pair-b-pill]', `${dollars(amt)} off`);
    setText(root, '[data-pair-b-sum]', money(sum));
    setText(root, '[data-pair-b-now]', money(pair));

    for (const p of data.partners) {
      const a = amounts(p);
      const chip = root.querySelector(`[data-pair-partner="${p.key}"]`);
      chip?.setAttribute('aria-checked', String(p.key === state.partnerKey));
      if (chip) {
        setText(chip, '[data-pair-chip-was]', money(a.price));
        setText(chip, '[data-pair-chip-plus]', `+ ${money(a.incr)}`);
      }
      const dialog = root.querySelector(`[data-pair-dialog="${p.key}"]`);
      if (!dialog) continue;
      setText(dialog, '[data-pair-d-pill]', `${dollars(a.amt)} off`);
      setText(dialog, '[data-pair-d-alone]', money(a.price));
      setText(dialog, '[data-pair-d-with]', `+ ${money(a.incr)}`);
      setText(dialog, '[data-pair-d-foot-pair]', money(a.pair));
      setText(dialog, '[data-pair-d-foot-sum]', money(a.sum));
      setText(dialog, '[data-pair-d-cta-amt]', money(a.pair));
    }

    if (atcLine) {
      atcLine.hidden = state.mode !== 'pair';
      setText(atcLine, '[data-pair-line-sum]', money(sum));
      setText(atcLine, '[data-pair-line-pair]', money(pair));
      setText(atcLine, '[data-pair-line-amt]', dollars(amt));
    }
    const button = buyButton();
    if (button instanceof HTMLButtonElement) {
      buyBtn.applyBuyButton(button, { pair: state.mode === 'pair', originalLabel: state.originalLabel, amountText: money(pair), force: state.busy });
    }
  }

  function select(mode, partnerKey) {
    state.mode = mode;
    if (partnerKey) state.partnerKey = partnerKey;
    render();
  }

  /** Post hero + partner together (vellatini-pair-add.js) with Horizon's loading state. */
  async function submitPair(key) {
    if (state.busy || !form || !formComponent) return;
    const p = partner(key);
    const button = buyButton();
    const ctas = root.querySelectorAll('[data-pair-dialog-cta]');
    state.busy = true;
    if (button) button.disabled = true;
    ctas.forEach((c) => (c.disabled = true));
    try {
      const heroId = Number(form.querySelector('input[name="id"]')?.value) || state.heroVariantId;
      // Attribution (_vt_source/_vt_paired_with) rides on BOTH lines so an
      // order can be read as "pair flow" even if one line is later removed.
      const heroProps = { ...add.heroLineProperties(form), ...add.sourceLineProperties('pdp_pair_hero', p.handle) };
      const partnerProps = {
        ...(add.partnerLineProperties(p.preorder) || {}),
        ...add.sourceLineProperties('pdp_pair_partner', data.hero.handle),
      };
      const items = [
        { id: heroId, quantity: 1, properties: heroProps },
        { id: p.variantId, quantity: 1, properties: partnerProps },
      ];
      const result = await add.addBoth({ items, dispatchOn: formComponent, productId: data.hero.productId });
      if (!result.ok) buyBtn.showAddError(formComponent, result.message || 'Add to cart failed');
    } catch (error) {
      console.error('[vellatini-pair-picker]', error);
      buyBtn.showAddError(formComponent, 'Add to cart failed');
    } finally {
      state.busy = false;
      if (button) button.disabled = false;
      ctas.forEach((c) => (c.disabled = false));
    }
  }

  root.addEventListener('click', (event) => {
    const t = /** @type {Element} */ (event.target);
    if (t instanceof HTMLDialogElement) return t.close(); // click on the scrim
    const details = t.closest('[data-pair-details]');
    if (details) return root.querySelector(`[data-pair-dialog="${details.dataset.pairDetails}"]`)?.showModal();
    if (t.closest('[data-pair-dialog-close]')) return t.closest('dialog')?.close();
    const cta = t.closest('[data-pair-dialog-cta]');
    if (cta) {
      select('pair', cta.dataset.pairDialogCta);
      t.closest('dialog')?.close();
      return submitPair(cta.dataset.pairDialogCta);
    }
    if (t.closest('a[href]')) return; // chip thumb link -> partner PDP
    const chip = t.closest('[data-pair-partner]');
    if (chip) return select('pair', chip.dataset.pairPartner);
    const opt = t.closest('[data-pair-mode]');
    if (opt) select(opt.dataset.pairMode);
  });

  root.addEventListener('keydown', (event) => {
    const target = /** @type {Element} */ (event.target);
    if (target.closest?.('a[href]')) return; // Enter on the thumb link follows it
    const chip = target.closest?.('[data-pair-partner]');
    if (chip && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      select('pair', chip.dataset.pairPartner);
    }
  });

  formComponent?.addEventListener('click', (event) => {
    const button = /** @type {Element} */ (event.target).closest('[ref="addToCartButton"]');
    if (!button || state.mode !== 'pair') return;
    event.preventDefault(); // belt and braces: cancels the implicit submit even if a morph restored type=submit
    submitPair(state.partnerKey);
  });

  document.addEventListener('shopify:product:select', async (event) => {
    const scope = /** @type {Element | null} */ (event.target)?.closest?.('.shopify-section');
    if (!scope?.contains(root) || typeof event.promise?.then !== 'function') return;
    let resolved;
    try {
      resolved = await event.promise;
    } catch {
      return;
    }
    const variant = resolved?.variant;
    if (variant) {
      const id = String(variant.id).replace(/\D/g, '');
      state.heroVariantId = Number(id) || state.heroVariantId;
      const cents = data.hero.variants?.[id];
      state.heroPrice = typeof cents === 'number' ? cents : Math.round(parseFloat(variant.price?.amount) * 100) || state.heroPrice;
    }
    const nextButton = resolved?.detail?.html?.querySelector?.('[ref="addToCartButton"]');
    const nextLabel = nextButton && buyBtn.labelNode(nextButton);
    if (nextLabel) state.originalLabel = nextLabel.textContent ?? state.originalLabel;
    setTimeout(render, 80); // after product-form.js's own morph of the button
  });

  rerender = render;
  render();
}
